/**
 * eraseMask — gomme non destructive sur les calques image.
 *
 * PRINCIPE
 * L'image source (`el.url`) n'est JAMAIS modifiée. On stocke à côté un masque
 * alpha (`el.eraseMask`, PNG en data-URL) : opaque = pixel conservé,
 * transparent = pixel effacé. Le calque reste donc réversible indéfiniment —
 * on peut réafficher ce qu'on a gommé, ou tout réinitialiser d'un clic.
 *
 * DEUX RENDUS, DEUX TECHNIQUES
 *  - À l'écran : CSS `mask-image` sur la balise image (cf. ImageLayer). Zéro
 *    recomposition, tout se passe sur le GPU, la gomme reste fluide même sur
 *    une image de 4000 px.
 *  - À l'export : composition d'un vrai PNG à canal alpha (`composeErased`),
 *    substitué à l'URL juste avant l'envoi. C'est indispensable : le chemin
 *    SVG (`build_svg`) et l'ancien chemin WeasyPrint ne connaissent pas
 *    `mask-image` — sans cette composition, les pixels effacés
 *    RÉAPPARAÎTRAIENT sur l'étiquette imprimée.
 *
 * GÉOMÉTRIE
 * Le masque couvre la BOÎTE du calque, pas les pixels de l'image. C'est ce qui
 * permet de l'étirer en `mask-size: 100% 100%` sans se soucier du cadrage
 * `object-contain` : les deux partagent le même repère, celui du cadre.
 */

// Grand côté du masque. 1400 px ≈ 300 dpi sur une étiquette de 118 mm : au-delà
// on paie du poids IndexedDB pour une finesse que l'impression ne restitue pas.
const MASK_MAX_PX = 1400;

// Grand côté du composite d'export. Plus généreux que le masque : ici on
// rééchantillonne l'image SOURCE, et c'est elle qui porte le détail.
const COMPOSITE_MAX_PX = 4000;

/** Dimensions du masque pour un calque, au ratio de sa boîte. */
export function maskSize(el) {
    const w = Math.max(1, el.width || 1);
    const h = Math.max(1, el.height || 1);
    const k = MASK_MAX_PX / Math.max(w, h);
    return { w: Math.max(2, Math.round(w * k)), h: Math.max(2, Math.round(h * k)) };
}

/** Canvas 2D hors écran, prêt à peindre. */
function blankCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
}

/**
 * Charge une image et résout quand elle est décodée.
 * `crossOrigin` est nécessaire pour que le canvas ne soit pas « taché »
 * (un canvas taché refuse toDataURL) — sans casser les data: et blob:.
 */
export function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (!/^(data:|blob:)/.test(src)) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image illisible'));
        img.src = src;
    });
}

/**
 * Prépare le canvas de travail d'un calque : reprend le masque existant, ou
 * part d'une surface entièrement opaque (rien d'effacé).
 * Le masque existant est réétiré si le calque a changé de proportions depuis.
 */
export async function openMaskCanvas(el) {
    const { w, h } = maskSize(el);
    const cvs = blankCanvas(w, h);
    const ctx = cvs.getContext('2d', { willReadFrequently: false });

    if (el.eraseMask) {
        try {
            const img = await loadImage(el.eraseMask);
            ctx.drawImage(img, 0, 0, w, h);
            return cvs;
        } catch (_) { /* masque corrompu : on repart d'une surface pleine */ }
    }
    // Le noir n'a aucune importance : seul l'alpha est lu par mask-image.
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    return cvs;
}

/**
 * Perce un disque dans le masque.
 * `hardness` : 'hard' = bord net · 'soft' = fondu sur la moitié externe.
 * Les coordonnées et le rayon sont en pixels du masque.
 */
export function punchDisc(ctx, x, y, radius, hardness) {
    const r = Math.max(0.5, radius);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    if (hardness === 'soft') {
        const g = ctx.createRadialGradient(x, y, r * 0.45, x, y, r);
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
    } else {
        ctx.fillStyle = 'rgba(0,0,0,1)';
    }
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/**
 * Perce un segment entre deux positions successives du pointeur.
 * Sans cette interpolation, un geste rapide laisse un pointillé : la souris
 * n'émet qu'une poignée d'événements par seconde de déplacement.
 *
 * Deux stratégies, pour une raison de coût :
 *  - bord NET : un seul trait à bouts ronds. Le résultat est identique à une
 *    traînée de disques, pour UNE opération de dessin au lieu de plusieurs
 *    dizaines. C'est ce qui rend le geste tenable à 120 Hz.
 *  - bord FLOU : le dégradé impose de semer des disques, mais il masque aussi
 *    les jointures — un pas d'un demi-rayon suffit largement.
 */
export function punchSegment(ctx, x0, y0, x1, y1, radius, hardness) {
    const r = Math.max(0.5, radius);

    if (hardness !== 'soft') {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
        ctx.lineWidth = r * 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        ctx.restore();
        return;
    }

    const dist = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.ceil(dist / Math.max(1, r * 0.5)));
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    for (let i = 1; i <= n; i++) {
        const t = i / n;
        const cx = x0 + (x1 - x0) * t;
        const cy = y0 + (y1 - y0) * t;
        const g = ctx.createRadialGradient(cx, cy, r * 0.45, cx, cy, r);
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

/** Le masque ne laisse-t-il rien passer ? (garde-fou : masque vierge inutile) */
export function isMaskPristine(cvs) {
    const ctx = cvs.getContext('2d', { willReadFrequently: true });
    const { data } = ctx.getImageData(0, 0, cvs.width, cvs.height);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 255) return false;
    }
    return true;
}

/**
 * Compose image + masque en un PNG à canal alpha, aux proportions de la boîte
 * du calque (donc rendu à l'identique par `object-contain`).
 * Retourne une data-URL, ou `null` si l'image ne peut pas être lue.
 */
export async function composeErased(url, maskDataUrl, boxW, boxH, { etirer = false } = {}) {
    let img, mask;
    try {
        [img, mask] = await Promise.all([loadImage(url), loadImage(maskDataUrl)]);
    } catch (_) {
        return null;
    }

    // Le composite adopte le ratio du CADRE ; sa définition est calée sur
    // l'image source pour ne pas dégrader ce que l'utilisateur a importé.
    const boxRatio = Math.max(0.01, boxW) / Math.max(0.01, boxH);
    let ch = img.naturalHeight || img.height || 1;
    let cw = ch * boxRatio;
    if (cw < (img.naturalWidth || img.width)) {
        cw = img.naturalWidth || img.width;
        ch = cw / boxRatio;
    }
    const k = Math.min(1, COMPOSITE_MAX_PX / Math.max(cw, ch));
    cw = Math.max(2, Math.round(cw * k));
    ch = Math.max(2, Math.round(ch * k));

    const cvs = blankCanvas(cw, ch);
    const ctx = cvs.getContext('2d');

    // 1) L'image, cadrée « contain » — même règle qu'à l'écran.
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const s = Math.min(cw / iw, ch / ih);
    const dw = iw * s;
    const dh = ih * s;
    // Un calque étiré (`etirer`, ImageLayer en object-fill) remplit son cadre, comme à l'écran.
    if (etirer) ctx.drawImage(img, 0, 0, cw, ch);
    else ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);

    // 2) Le masque, étiré sur toute la surface : ne garder que l'opaque.
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0, cw, ch);

    try {
        return cvs.toDataURL('image/png');
    } catch (_) {
        return null; // canvas taché (image distante sans CORS)
    }
}

/**
 * Aplatit les gommes d'une liste d'éléments, juste avant un export.
 * Les calques sans masque sont renvoyés tels quels (aucune recompression).
 * Un échec de composition laisse l'élément intact plutôt que de faire échouer
 * tout l'export : mieux vaut une image non gommée qu'aucun PDF.
 */
export async function flattenEraseMasks(elements) {
    if (!Array.isArray(elements)) return elements;
    return Promise.all(elements.map(async (el) => {
        if (!el || el.type !== 'image' || !el.eraseMask || !el.url) return el;
        const flat = await composeErased(el.url, el.eraseMask, el.width, el.height, { etirer: Boolean(el.etirer) });
        if (!flat) return el;
        const { eraseMask, ...rest } = el;
        return { ...rest, url: flat };
    }));
}
